import React, { useRef } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import {
  X,
  Users,
  MessageSquare,
  BarChart,
  ShoppingBag,
  CreditCard,
  TrendingUp,
  Heart,
  Check
} from "lucide-react";
import { useOrganizationStore } from "@/store/useOrganizationStore";
import { usePermissions } from "@/hooks/usePermissions";
import FeatureUtils from "@/utility/FeatureUtils";
import { cn } from "@/lib/utils";
import { getFeatureCardRoute } from "@/utils/featureCardRoutes";
import {
  OnboardingProgressService,
  ACTION_TO_PROGRESS_MAP
} from "@/services/OnboardingProgressService";
import { OnboardingProgress } from "@/services/OrganizationService";

interface OnboardingBannerProps {
  onDismiss?: () => void;
  onStartGrowing?: () => void;
  isDismissed?: boolean;
  onButtonRef?: (ref: React.RefObject<HTMLButtonElement | null>) => void;
  timeRemaining?: number | null;
}

interface FeatureCard {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: "purple" | "blue" | "pink" | "green" | "multi-color";
  isCompleted: boolean;
  isBlurred: boolean;
}

// CRM App Feature Cards
const CRM_FEATURE_CARDS: Omit<FeatureCard, "isCompleted" | "isBlurred">[] = [
  { id: "customers", label: "Customer List", icon: Users, color: "purple" },
  { id: "sms", label: "SMS Campaign", icon: MessageSquare, color: "pink" },
  {
    id: "reports",
    label: "Customer Reports",
    icon: BarChart,
    color: "multi-color"
  },
  { id: "products", label: "Products", icon: ShoppingBag, color: "blue" },
  {
    id: "transactions",
    label: "Transactions",
    icon: CreditCard,
    color: "green"
  },
  { id: "sales", label: "Sales Reports", icon: TrendingUp, color: "purple" },
  { id: "automated", label: "Automated Messages", icon: Heart, color: "blue" }
];

// Analytics App Feature Cards
const ANALYTICS_FEATURE_CARDS: Omit<
  FeatureCard,
  "isCompleted" | "isBlurred"
>[] = [
  {
    id: "customer-overview",
    label: "Customer Overview",
    icon: Users,
    color: "purple"
  },
  {
    id: "behavior-engagement",
    label: "Behavior & Engagement",
    icon: MessageSquare,
    color: "pink"
  },
  {
    id: "purchase-patterns",
    label: "Purchase Patterns",
    icon: BarChart,
    color: "multi-color"
  },
  {
    id: "sales-overview",
    label: "Sales Overview",
    icon: TrendingUp,
    color: "purple"
  },
  {
    id: "trends-patterns",
    label: "Trends & Patterns",
    icon: BarChart,
    color: "blue"
  },
  {
    id: "product-insights",
    label: "Product Insights",
    icon: ShoppingBag,
    color: "green"
  }
];

const calculateProgressFromOnboardingProgress = (
  onboardingProgress: any,
  appType: "CRM" | "ANALYTICS"
): { completed: number; total: number; percentage: number } => {
  if (!onboardingProgress) {
    return {
      completed: 0,
      total: appType === "ANALYTICS" ? 6 : 7,
      percentage: 0
    };
  }

  let allSteps: string[] = [];
  let total = 7;

  if (appType === "CRM") {
    allSteps = [
      onboardingProgress.customer,
      onboardingProgress.customersReportOverview,
      onboardingProgress.smsCampaigns,
      onboardingProgress.productCatalog,
      onboardingProgress.transactions,
      onboardingProgress.salesReportOverview,
      onboardingProgress.automatedCampaigns
    ];
    total = 7;
  } else if (appType === "ANALYTICS") {
    allSteps = [
      onboardingProgress.customer,
      onboardingProgress.behaviorAndEngagementReport,
      onboardingProgress.purchasePatternReport,
      onboardingProgress.salesReportOverview,
      onboardingProgress.trendsAndPatternsReport,
      onboardingProgress.productInsightsReport
    ];
    total = 6;
  }

  const completed = allSteps.filter((step) => step === "DONE").length;
  const percentage = Math.round((completed / total) * 100);

  return { completed, total, percentage };
};

const getFeatureCompletionStatusFromProgress = (
  onboardingProgress: any,
  appType: "CRM" | "ANALYTICS"
): FeatureCard[] => {
  const featureCards =
    appType === "ANALYTICS" ? ANALYTICS_FEATURE_CARDS : CRM_FEATURE_CARDS;

  if (!onboardingProgress) {
    return featureCards.map((feature) => ({
      ...feature,
      isCompleted: false,
      isBlurred: true
    }));
  }

  // Calculate setup completion based on app type
  let isSetupComplete = false;
  if (appType === "CRM") {
    const step1Complete =
      onboardingProgress.customer === "DONE" &&
      onboardingProgress.customersReportOverview === "DONE" &&
      onboardingProgress.smsCampaigns === "DONE";
    const step2Complete =
      onboardingProgress.productCatalog === "DONE" &&
      onboardingProgress.transactions === "DONE" &&
      onboardingProgress.salesReportOverview === "DONE" &&
      onboardingProgress.automatedCampaigns === "DONE";
    isSetupComplete = step1Complete && step2Complete;
  } else if (appType === "ANALYTICS") {
    const step1Complete =
      onboardingProgress.customer === "DONE" &&
      onboardingProgress.behaviorAndEngagementReport === "DONE" &&
      onboardingProgress.purchasePatternReport === "DONE";
    const step2Complete =
      onboardingProgress.salesReportOverview === "DONE" &&
      onboardingProgress.trendsAndPatternsReport === "DONE" &&
      onboardingProgress.productInsightsReport === "DONE";
    isSetupComplete = step1Complete && step2Complete;
  }

  return featureCards.map((feature) => {
    let isCompleted = false;

    if (appType === "CRM") {
      if (feature.id === "customers")
        isCompleted = onboardingProgress.customer === "DONE";
      else if (feature.id === "sms")
        isCompleted = onboardingProgress.smsCampaigns === "DONE";
      else if (feature.id === "reports")
        isCompleted = onboardingProgress.customersReportOverview === "DONE";
      else if (feature.id === "products")
        isCompleted = onboardingProgress.productCatalog === "DONE";
      else if (feature.id === "transactions")
        isCompleted = onboardingProgress.transactions === "DONE";
      else if (feature.id === "sales")
        isCompleted = onboardingProgress.salesReportOverview === "DONE";
      else if (feature.id === "automated")
        isCompleted = onboardingProgress.automatedCampaigns === "DONE";
    } else if (appType === "ANALYTICS") {
      if (feature.id === "customer-overview")
        isCompleted = onboardingProgress.customer === "DONE";
      else if (feature.id === "behavior-engagement")
        isCompleted = onboardingProgress.behaviorAndEngagementReport === "DONE";
      else if (feature.id === "purchase-patterns")
        isCompleted = onboardingProgress.purchasePatternReport === "DONE";
      else if (feature.id === "sales-overview")
        isCompleted = onboardingProgress.salesReportOverview === "DONE";
      else if (feature.id === "trends-patterns")
        isCompleted = onboardingProgress.trendsAndPatternsReport === "DONE";
      else if (feature.id === "product-insights")
        isCompleted = onboardingProgress.productInsightsReport === "DONE";
    }

    return {
      ...feature,
      isCompleted,
      isBlurred: !isSetupComplete
    };
  });
};

const capitalizeWords = (str: string): string => {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Map feature card IDs to action IDs used in ACTION_TO_PROGRESS_MAP
const FEATURE_CARD_TO_ACTION_MAP: Record<string, string> = {
  // CRM mappings
  customers: "customers",
  sms: "sms",
  reports: "reports",
  products: "products",
  transactions: "transactions",
  sales: "sales-overview",
  automated: "automated-campaigns",
  // Analytics mappings
  "customer-overview": "customer-overview",
  "behavior-engagement": "behaviour-engagement", // Note: map uses "behaviour" spelling
  "purchase-patterns": "purchase-patterns",
  "sales-overview": "sales-overview",
  "trends-patterns": "trends-patterns",
  "product-insights": "product-insights"
};

export const OnboardingBanner: React.FC<OnboardingBannerProps> = ({
  onDismiss,
  onStartGrowing,
  isDismissed = false,
  onButtonRef,
  timeRemaining
}) => {
  const {
    organization,
    showOnboardingFlow,
    onboardingProgress,
    setOnboardingProgress
  } = useOrganizationStore();
  const { permissions } = usePermissions();
  const startGrowingButtonRef = useRef<HTMLButtonElement>(null);

  // Check appType using FeatureUtils methods
  const isCRM = FeatureUtils.isCRMEnabled(permissions);
  const isAnalytics = FeatureUtils.isAnalyticsEnabled(permissions);

  let appType: "CRM" | "ANALYTICS" = "CRM";
  if (isAnalytics) {
    appType = "ANALYTICS";
  } else if (isCRM) {
    appType = "CRM";
  }

  // Expose the button ref to parent component
  React.useEffect(() => {
    if (onButtonRef) {
      onButtonRef(startGrowingButtonRef);
    }
  }, [onButtonRef]);

  // Use onboardingProgress directly for more accurate progress calculation
  const progress = calculateProgressFromOnboardingProgress(
    onboardingProgress,
    appType
  );
  const featureCards = getFeatureCompletionStatusFromProgress(
    onboardingProgress,
    appType
  );

  const isCloverBuild = import.meta.env.__IS_CLOVER_BUILD__;

  // Handle feature card click - navigate and update progress
  const handleFeatureCardClick = async (
    _e: React.MouseEvent,
    featureId: string
  ) => {
    // Special case: For Analytics "customer-overview", use "customer" progress key directly
    // This matches how OnboardingSidePanel handles it (task id "customer" uses progress key "customer")
    let progressKey: keyof OnboardingProgress | undefined;
    let actionId: string | undefined;

    if (appType === "ANALYTICS" && featureId === "customer-overview") {
      // For Analytics, "customer-overview" maps to "customer" progress key
      // Use "customers" action ID (which maps to "customer" progress key)
      progressKey = "customer";
      actionId = "customers";
    } else {
      // Get the action ID from the feature card ID
      actionId = FEATURE_CARD_TO_ACTION_MAP[featureId];
      if (!actionId) {
        console.warn(`No action ID found for feature card: ${featureId}`);
        return;
      }

      // Check if this action maps to a progress key
      progressKey = ACTION_TO_PROGRESS_MAP[actionId];
      if (!progressKey) {
        console.warn(`No progress key found for action: ${actionId}`);
        // Still navigate even if we can't track progress
        return;
      }
    }

    // Only update progress if not already completed and we have valid keys
    if (!progressKey || !actionId) {
      return;
    }

    const currentProgress: Partial<OnboardingProgress> =
      onboardingProgress || {};
    if (currentProgress[progressKey] !== "DONE") {
      try {
        // Mark action as completed using the service
        await OnboardingProgressService.getInstance().markActionCompleted(
          actionId
        );

        // Update local state immediately for better UX
        const updatedProgress = {
          ...currentProgress,
          [progressKey]: "DONE"
        } as OnboardingProgress;
        setOnboardingProgress(updatedProgress);

        // Check if all steps are completed
        const newProgress = calculateProgressFromOnboardingProgress(
          updatedProgress,
          appType
        );
        if (newProgress.completed === newProgress.total) {
          // Mark onboarding as complete
          await OnboardingProgressService.getInstance().checkAndUpdateOnboardingCompletion();
        }
      } catch (error) {
        console.error("Failed to update onboarding progress:", error);
        // Still allow navigation even if progress update fails
      }
    }

    // Navigation will happen via the Link component
  };

  // Format time remaining for display
  const formatTimeRemaining = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // Don't show banner if onboarding flow is disabled or banner is dismissed
  if (!showOnboardingFlow || isDismissed) {
    return null;
  }

  return (
    <div
      data-testid="welcome-banner"
      role="banner"
      aria-label="Welcome banner"
      className={cn(
        "bg-white border-t-4 border-teal-500 rounded-lg shadow-lg p-6 mb-6",
        "animate-in fade-in slide-in-from-top duration-500 ease-out",
        "transform-gpu will-change-transform backface-visibility-hidden",
        isCloverBuild &&
          "clover-compatible clover-spacing clover-colors clover-font-weight clover-border-radius"
      )}
    >
      {/* Header Section */}
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <h2
            className={cn(
              "text-xl font-bold text-gray-900 mb-2 flex items-center gap-2",
              isCloverBuild && "text-2xl clover-text-large clover-font-weight"
            )}
          >
            Hi{" "}
            {capitalizeWords(
              organization?.name || organization?.shortName || "there"
            )}
            ! 👋
          </h2>
          <p className="text-base text-gray-600">
            You're just a few quick steps away from getting started with
            MarketBuzz.
            {timeRemaining && timeRemaining > 0 && (
              <span className="ml-2 text-sm text-teal-600 font-medium">
                (Auto-hide in {formatTimeRemaining(timeRemaining)})
              </span>
            )}
          </p>
        </div>

        {onDismiss && (
          <Button
            onClick={onDismiss}
            variant="ghost"
            size="sm"
            className={cn(
              "text-gray-400 hover:text-gray-600 hover:scale-110 hover:bg-gray-100 transition-all duration-200",
              isCloverBuild && "min-h-[48px] min-w-[48px]"
            )}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Feature Cards Section */}
      <div className="flex items-center justify-between mb-2">
        <div
          data-testid="feature-cards-grid"
          className={cn(
            "grid grid-cols-7 gap-4",
            isCloverBuild && "landscape-grid"
          )}
        >
          {featureCards.map((feature, index) => {
            const route = getFeatureCardRoute(feature.id);
            return (
              <Link
                key={feature.id}
                to={route}
                onClick={(e) => handleFeatureCardClick(e, feature.id)}
                className="flex flex-col items-center no-underline text-inherit"
                aria-label={`Navigate to ${feature.label}${feature.isCompleted ? " - Completed" : " - Incomplete"}`}
              >
                <div
                  className={cn(
                    "w-12 h-12 rounded-full border-2 flex items-center justify-center mb-2 relative",
                    "transition-all duration-200",
                    // Show brand colors for all cards based on their color type
                    feature.color === "purple" &&
                      "border-purple-300 bg-purple-100",
                    feature.color === "blue" && "border-blue-300 bg-blue-100",
                    feature.color === "pink" && "border-pink-300 bg-pink-100",
                    feature.color === "green" &&
                      "border-green-300 bg-green-100",
                    feature.color === "multi-color" &&
                      "border-gray-300 bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100",
                    // Blur effect for incomplete cards
                    !feature.isCompleted &&
                      feature.isBlurred &&
                      "blur-[0.5px] opacity-70 hover:blur-none hover:opacity-100",
                    !feature.isCompleted &&
                      !feature.isBlurred &&
                      "hover:scale-105 hover:shadow-md cursor-pointer",
                    isCloverBuild && "w-16 h-16"
                  )}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <feature.icon
                    className={cn(
                      "w-6 h-6 transition-colors duration-200",
                      // Show brand colors for all icons
                      feature.color === "purple" && "text-purple-600",
                      feature.color === "blue" && "text-blue-600",
                      feature.color === "pink" && "text-pink-600",
                      feature.color === "green" && "text-green-600",
                      feature.color === "multi-color" && "text-gray-600"
                    )}
                  />
                  {feature.isCompleted && (
                    <Check
                      className={cn(
                        "absolute -top-1 -right-1 h-4 w-4 text-white rounded-full",
                        feature.color === "purple" && "bg-purple-600",
                        feature.color === "blue" && "bg-blue-600",
                        feature.color === "pink" && "bg-pink-600",
                        feature.color === "green" && "bg-green-600",
                        feature.color === "multi-color" && "bg-gray-600"
                      )}
                    />
                  )}
                </div>
                <span className="text-xs text-gray-600 text-center leading-tight">
                  {feature.label}
                </span>
              </Link>
            );
          })}
        </div>

        <Button
          ref={startGrowingButtonRef}
          onClick={onStartGrowing}
          data-testid="start-growing-button"
          className={cn(
            "bg-teal-600 hover:bg-teal-700 text-white px-4 py-1.5 rounded-md text-sm font-medium",
            "hover:scale-105 hover:shadow-lg transition-all duration-200",
            "flex items-center gap-1",
            isCloverBuild && "min-h-[48px] min-w-[48px]"
          )}
        >
          Get Started
        </Button>
      </div>

      {/* Progress and Action Section */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          <div
            data-testid="progress-indicator"
            className="text-sm font-medium text-gray-700 whitespace-nowrap"
          >
            {progress.completed}/{progress.total} ({progress.percentage}%)
          </div>
          <div className="flex-1 max-w-[calc(7*4.5rem+6*1.5rem)] h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-teal-500 transition-all duration-300"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
